import { timezoneHeader } from "./timezone";

const BASE = "";

/**
 * FRONT-895 review fix: `body` carries the parsed JSON error body (when the
 * response was JSON-shaped) alongside the existing human-readable `message` —
 * so a caller that needs a structured field off a non-2xx response (e.g. the
 * `{reason, existing}` shape on a 409) can go through the shared `request()`
 * path (401 silent-refresh-then-retry included) instead of dropping to
 * `api.raw`. Exported so callers can `instanceof` it.
 */
export class ApiError extends Error {
  /**
   * FRONT-1279 review / Sentry sweep: true on a 401 we EXPECTED — the session
   * is gone or was never established, so the app is behaving correctly by
   * refusing. Only ever set by `unauthorized()` below; every other error
   * leaves it false. Read by the query client to decide what reaches Sentry.
   */
  public expectedAuthFailure = false;

  constructor(
    public status: number,
    message: string,
    public body?: unknown,
    /**
     * FRONT-1540: the request path this failed on. Every ApiError is thrown
     * from the one `request()` below, so Sentry groups them all by that single
     * stack frame — one issue whose title is just whichever message arrived
     * last. The path (normalized, see `apiErrorFingerprint` in query-client)
     * is what lets that group split into the distinct bugs it was hiding.
     * Optional so existing constructions keep working.
     */
    public path?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Build the 401 thrown out of `request()`.
 *
 * `expected` is the whole point: it is true whenever we have NO evidence the
 * session is valid (it expired, the refresh endpoint itself refused, we are
 * already on the auth-error page, or the refresh attempt told us nothing).
 * Those 401s are the app working — the user has to sign in again — and there
 * is no frontend change that could act on one, so they must not be reported.
 *
 * It is false in exactly one case: a silent refresh SUCCEEDED and the very
 * same route still answered 401. That is proof the session is valid and the
 * route refused anyway — a real authorization defect, and it still reports.
 *
 * `path` is required rather than optional: FRONT-1540 fingerprints on it, and
 * the 401s routed through here are the ones whose grouping mattered most.
 */
function unauthorized(expected: boolean, path: string): ApiError {
  const err = new ApiError(401, "Unauthorized", undefined, path);
  err.expectedAuthFailure = expected;
  return err;
}

/** Parse `text` as a JSON object/array; anything else (plain text, HTML,
 *  unparsable) yields `undefined` rather than throwing. */
function parseJsonBody(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return undefined;
  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

/**
 * FRONT-340: what a silent-refresh attempt actually told us.
 * - "ok"        → session renewed, retry the original request.
 * - "expired"   → the refresh endpoint itself said 401/403 — the session is
 *                 definitively gone. Only THIS may route the user to login.
 * - "transient" → 429 / 5xx / network failure. The server is rate-limited or
 *                 unwell; we know NOTHING about the session. Treating this as
 *                 "logged out" is what turned one rate-limited IP into a
 *                 login loop for every browser behind it.
 */
export type RefreshOutcome = "ok" | "expired" | "transient";

let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * FRONT-340: once the session is definitively lost, latch. Every later 401
 * fails fast — no refresh attempt, no repeated session-lost events. Without
 * the latch, the gate's /me re-check itself 401'd → refresh → session-lost
 * again → re-check again: a zero-delay self-sustaining loop that produced
 * 123k+ requests from one zombie tab overnight. A succeeding response
 * unlatches (e.g. the user logged back in from another tab).
 */
let sessionLost = false;

/**
 * FRONT-1101: a hung server-side refresh (observed live: minutes) must not
 * wedge the single-flighted `refreshInFlight` promise forever — every 401'd
 * request in the app awaits it, so a hang here hangs the whole app (the
 * "Loading your conversation…" spinner never settling). Overridable only for
 * tests, which can't afford to actually wait ~12s for a real abort.
 */
let refreshTimeoutMs = 12_000;

/**
 * Silent session renewal, single-flight per tab. Exported (FRONT-157) so
 * the SSE hook can renew before reconnecting instead of hammering 401s.
 */
export async function tryRefreshToken(): Promise<RefreshOutcome> {
  if (sessionLost) return "expired";
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = fetch(`${BASE}/api/auth/refresh`, {
    signal: AbortSignal.timeout(refreshTimeoutMs),
  })
    .then((r): RefreshOutcome => {
      if (r.ok) return "ok";
      if (r.status === 401 || r.status === 403) return "expired";
      return "transient";
    })
    // A timeout/abort lands here exactly like a network failure — it proves
    // nothing about the session (the server may be slow, not the session
    // being dead), so it classifies as "transient", never "expired".
    .catch((): RefreshOutcome => "transient")
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

/**
 * FRONT-157: fired when a request 401'd AND the silent refresh answered a
 * definitive 401 — i.e. the session is gone, not a single flaky endpoint.
 * The auth gate (app-layout) listens and re-checks /api/auth/me, which
 * flips the app to the login redirect and stops all polling. Kept as a
 * window event because this module has no access to the query client.
 * Fires at most once per page lifetime (the FRONT-340 latch).
 */
export const SESSION_LOST_EVENT = "eddy:session-lost";

export function signalSessionLost(): void {
  if (sessionLost) return;
  sessionLost = true;
  window.dispatchEvent(new Event(SESSION_LOST_EVENT));
}

/**
 * FRONT-1231: read the FRONT-340 latch. A caller that already has a failed
 * request in hand needs to tell "the refresh endpoint definitively said the
 * session is gone" apart from "the refresh attempt told us nothing" — only
 * this latch knows, and both cases surface as an identical `ApiError(401)`.
 * Read-only on purpose: the latch is still owned (set and lifted) by
 * `request()` and `signalSessionLost()` below.
 */
export function isSessionLost(): boolean {
  return sessionLost;
}

/** Test hook — module state would otherwise leak across vitest cases. */
export function resetSessionLostLatchForTests(): void {
  sessionLost = false;
  refreshInFlight = null;
  refreshTimeoutMs = 12_000;
}

/**
 * Test hook — shrink the refresh-fetch abort timeout so a "hangs forever"
 * test doesn't have to wait ~12 real seconds for the abort to fire. Reset by
 * `resetSessionLostLatchForTests`.
 */
export function setRefreshTimeoutMsForTests(ms: number): void {
  refreshTimeoutMs = ms;
}

/**
 * Turn an error response body into something a human can read in the UI.
 * Infra layers (Cloudflare, nginx) answer 5xx with FULL HTML PAGES — before
 * this, that entire document was thrown as the error message and rendered
 * verbatim in every error state ("Failed to load X: <!DOCTYPE html>…").
 * JSON `{error}` bodies from our own API keep their message; anything
 * HTML-shaped or oversized collapses to a short status-based sentence.
 */
export function humanErrorMessage(status: number, body: string): string {
  const statusText: Record<number, string> = {
    502: "the server didn't respond (bad gateway)",
    503: "the service is temporarily unavailable",
    504: "the server took too long to respond (gateway timeout)",
  };
  const fallback = `Request failed (error ${status}${statusText[status] ? ` — ${statusText[status]}` : ""}). Please try again shortly.`;

  const trimmed = (body ?? "").trim();
  if (!trimmed) return fallback;
  // Infra error pages are HTML documents — never show those to a human.
  if (/^<!doctype html|^<html/i.test(trimmed)) return fallback;
  // Our own API errors are JSON with an `error` message — unwrap it so the
  // UI shows the sentence, not the JSON braces around it. Several routes
  // (agents/routes.ts, reflect/routes.ts, coding/routes.ts, …) instead use
  // `{ ok: false, reason }` for a declined/already-consumed action — same
  // shape, different key, so unwrap that too rather than falling through to
  // the raw JSON text.
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as { error?: unknown; reason?: unknown };
      let message: string | undefined;
      if (typeof parsed.error === "string" && parsed.error.trim()) {
        message = parsed.error;
      } else if (typeof parsed.reason === "string" && parsed.reason.trim()) {
        message = parsed.reason;
      }
      if (message) return message.length <= 500 ? message : `${message.slice(0, 500)}…`;
    } catch {
      /* fall through to the generic handling below */
    }
  }
  // Short plain-text bodies can pass through; anything bulky gets replaced.
  if (trimmed.length <= 300 && !trimmed.includes("<")) return trimmed;
  return fallback;
}

/**
 * The `ApiError` for a non-2xx response: status, the human sentence, the
 * parsed body and the path — everything the query client reads to decide
 * what reaches Sentry and how it groups. Shared so a caller that has to use a
 * raw fetch (a file download or upload) throws the same shape as `request()`
 * (FRONT-2811: a plain `Error` there sent a person's wrong-file upload to
 * Sentry and grouped unrelated failures into one issue).
 *
 * A 401 only arrives here from such a raw caller, since `request()` handles
 * its own. No silent refresh was tried, so nothing proves the session is
 * valid, and by `unauthorized()`'s rule that 401 is expected.
 */
export async function apiErrorFromResponse(res: Response, path: string): Promise<ApiError> {
  const text = await res.text().catch(() => res.statusText);
  const err = new ApiError(res.status, humanErrorMessage(res.status, text), parseJsonBody(text), path);
  if (res.status === 401) err.expectedAuthFailure = true;
  return err;
}

async function request<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  // A multipart body sets its own Content-Type with the boundary (FRONT-2740).
  const isForm = typeof FormData !== "undefined" && init?.body instanceof FormData;
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(isForm ? {} : { "Content-Type": "application/json" }),
      ...timezoneHeader(),
      ...init?.headers,
    },
  });

  if (res.status === 401) {
    if (window.location.pathname.startsWith("/auth-error")) {
      throw unauthorized(true, path);
    }

    if (path === "/api/auth/refresh") {
      throw unauthorized(true, path);
    }

    // Try one silent refresh + retry. A single endpoint returning 401
    // (missing claim, route bug, race) shouldn't drag the whole session
    // out — but a refresh that itself answers 401 means the session is
    // gone, so we signal the auth gate to re-check /me and route to login
    // (FRONT-157). Without that signal the gate's cached "authenticated"
    // answer kept the app alive as a zombie: dead dashboard, every poll
    // 401ing. A rate-limited/5xx refresh proves nothing about the session
    // (FRONT-340) — fail this one request and let the caller retry later.
    // `retried` means the refresh worked and this route STILL refused — the
    // one 401 that names a defect. `sessionLost` is the opposite: the latch is
    // only set after the refresh endpoint definitively said the session is
    // gone.
    if (retried || sessionLost) {
      throw unauthorized(!retried, path);
    }

    const refreshed = await tryRefreshToken();
    if (refreshed === "ok") {
      return request<T>(path, init, true);
    }
    if (refreshed === "expired") {
      signalSessionLost();
    }
    // Either the session is definitively gone ("expired"), or the refresh was
    // rate-limited/5xx/aborted and proved nothing ("transient"). Neither is
    // evidence the session is valid, so neither is actionable here.
    throw unauthorized(true, path);
  }

  if (!res.ok) throw await apiErrorFromResponse(res, path);

  // A succeeding call means the session works again (login completed in
  // another tab, cookie restored) — lift the FRONT-340 latch so silent
  // refresh is available for the next genuine expiry.
  sessionLost = false;

  if (res.status === 204) return undefined as T;

  return res.json();
}

export const api = {
  // `signal` lets a caller abort an in-flight request (e.g. the connect flow's
  // Cancel button); omit it and behaviour is unchanged.
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
      signal,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  // A multipart POST through the same path as every other call: silent
  // refresh on 401, and an ApiError with the status on a refusal (FRONT-2740).
  form: <T>(path: string, body: FormData) => request<T>(path, { method: "POST", body }),
  // Streaming calls that bypass `request()` (FRONT-1507: still needs the
  // timezone header — e.g. `use-ask-stream.ts`'s agent endpoint).
  raw: (path: string, init?: RequestInit) =>
    fetch(`${BASE}${path}`, {
      ...init,
      headers: mergeHeaders(timezoneHeader(), init?.headers),
    }),
};

/**
 * FRONT-1507 review MINOR 10: `init?.headers` is `RequestInit`'s
 * `HeadersInit` union — a plain object (what both current callers pass), a
 * real `Headers` instance, or an array of `[key, value]` pairs. The old
 * `{...timezoneHeader(), ...init?.headers}` spread only handled the plain-
 * object shape correctly: spreading a `Headers` instance yields no own
 * enumerable properties (every header silently dropped), and spreading an
 * array yields numeric-indexed keys, not header names. `Headers` itself
 * normalizes all three input shapes, so building one and reading it back
 * out handles every case (and makes name collisions merge case-
 * insensitively, matching real HTTP semantics) — `extra` wins over `base`.
 */
function mergeHeaders(base: Record<string, string>, extra?: HeadersInit): Record<string, string> {
  const merged = new Headers(base);
  if (extra) {
    new Headers(extra).forEach((value, key) => merged.set(key, value));
  }
  return Object.fromEntries(merged.entries());
}
